import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Autocomplete,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  CircularProgress,
  Alert,
  Divider,
  Chip
} from '@mui/material';
import {
  LocalShipping as LocalShippingIcon,
  Send as SendIcon
} from '@mui/icons-material';
import Swal from 'sweetalert2';

const ModalNuevoPTV = ({ open, onClose, onGuardado, server, user, filtros }) => {
  const [loadingCierres, setLoadingCierres] = useState(false);
  const [loadingCuentas, setLoadingCuentas] = useState(false);
  const [guardando, setGuardando] = useState(false);

  // Catálogos
  const [cierresRevisados, setCierresRevisados] = useState([]);
  const [cuentasBancarias, setCuentasBancarias] = useState([]);
  const [sucursales, setSucursales] = useState([]);

  // Selecciones
  const [cierresSeleccionados, setCierresSeleccionados] = useState([]);
  const [sucursalSeleccionada, setSucursalSeleccionada] = useState(null);
  const [cuentaBancariaSeleccionada, setCuentaBancariaSeleccionada] = useState(null);

  // Campos del formulario
  const [empresaServicio, setEmpresaServicio] = useState('');
  const [trazabilidad, setTrazabilidad] = useState('');
  const [fechaEnvio, setFechaEnvio] = useState(() => new Date().toISOString().split('T')[0]);
  const [observacion, setObservacion] = useState('');

  useEffect(() => {
    if (open) {
      cargarCuentasBancarias();
      cargarSucursales();
    }
  }, [open]);

  // Cuando cambia la sucursal seleccionada, recargar los cierres revisados
  useEffect(() => {
    if (sucursalSeleccionada) {
      cargarCierresRevisados(sucursalSeleccionada.idSucursal);
    } else {
      setCierresRevisados([]);
      setCierresSeleccionados([]);
    }
  }, [sucursalSeleccionada]);

  const cargarSucursales = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${server}/MgSucursales`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const arr = data.$values || data || [];
        setSucursales(arr);
        // Pre-seleccionar si viene un filtro
        if (filtros?.idSucursal) {
          const match = arr.find(s => s.idSucursal === filtros.idSucursal);
          if (match) setSucursalSeleccionada(match);
        }
      }
    } catch (err) {
      console.error('Error al cargar sucursales:', err);
    }
  };

  const cargarCuentasBancarias = async () => {
    setLoadingCuentas(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${server}/ChkBancosCuentasBancarias`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCuentasBancarias(data.$values || data || []);
      }
    } catch (err) {
      console.error('Error al cargar cuentas bancarias:', err);
    } finally {
      setLoadingCuentas(false);
    }
  };

  const cargarCierresRevisados = async (idSucursal) => {
    setLoadingCierres(true);
    setCierresRevisados([]);
    setCierresSeleccionados([]);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${server}/TransporteValores/GetCierresRevisados?idSucursal=${idSucursal}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error('Error al obtener cierres revisados');
      const result = await response.json();
      if (result.success) {
        const datos = result.datos?.$values || result.datos || [];
        setCierresRevisados(datos);
      } else {
        throw new Error(result.message || 'Error al cargar cierres');
      }
    } catch (err) {
      console.error(err);
      Swal.fire({ icon: 'error', title: 'Error', text: err.message });
    } finally {
      setLoadingCierres(false);
    }
  };

  const toggleCierre = (cierre) => {
    setCierresSeleccionados(prev => {
      const existe = prev.find(c => c.idCierre === cierre.idCierre);
      if (existe) return prev.filter(c => c.idCierre !== cierre.idCierre);
      return [...prev, cierre];
    });
  };

  const toggleTodos = () => {
    if (cierresSeleccionados.length === cierresRevisados.length) {
      setCierresSeleccionados([]);
    } else {
      setCierresSeleccionados([...cierresRevisados]);
    }
  };

  const totalEfectivoSeleccionado = useMemo(
    () => cierresSeleccionados.reduce((sum, c) => sum + (c.efectivo || 0), 0),
    [cierresSeleccionados]
  );

  const formatNumber = (val) =>
    new Intl.NumberFormat('es-HN', { style: 'currency', currency: 'HNL' }).format(val || 0);

  const handleGuardar = async () => {
    // Validaciones
    if (!sucursalSeleccionada) {
      Swal.fire({ icon: 'warning', title: 'Validación', text: 'Seleccione una sucursal' });
      return;
    }
    if (cierresSeleccionados.length === 0) {
      Swal.fire({ icon: 'warning', title: 'Validación', text: 'Seleccione al menos un cierre' });
      return;
    }
    if (!cuentaBancariaSeleccionada) {
      Swal.fire({ icon: 'warning', title: 'Validación', text: 'Seleccione una cuenta bancaria' });
      return;
    }
    if (!trazabilidad.trim()) {
      Swal.fire({ icon: 'warning', title: 'Validación', text: 'Ingrese el número de trazabilidad' });
      return;
    }
    if (!empresaServicio.trim()) {
      Swal.fire({ icon: 'warning', title: 'Validación', text: 'Ingrese el nombre de la empresa de servicio' });
      return;
    }
    if (!fechaEnvio) {
      Swal.fire({ icon: 'warning', title: 'Validación', text: 'Ingrese la fecha de envío' });
      return;
    }

    setGuardando(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        idCuentaBancaria: cuentaBancariaSeleccionada.idCuentaBancaria,
        idSucursal: sucursalSeleccionada.idSucursal,
        totalEfectivoEnvio: totalEfectivoSeleccionado,
        observacion: observacion.trim() || null,
        trazabilidad: trazabilidad.trim(),
        codigoInterno: null,
        fechaEnvio: new Date(fechaEnvio).toISOString(),
        fechaDeposito: null,
        fechaArqueo: null,
        empresaServicio: empresaServicio.trim(),
        sistema: cierresSeleccionados[0]?.sistema || 'Web',
        ingresadoPor: user?.codigoUsuario || null,
        modificadoPor: user?.codigoUsuario || null,
        modificacion: 1,
        transporteValoresDetalles: cierresSeleccionados.map(c => ({
          idCajaEncabezadoArqueo: c.idCierre,
          valorEfectivo: c.efectivo
        }))
      };

      const response = await fetch(`${server}/TransporteValores/PTV-envio`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || result.error || 'Error al guardar el envío');
      }

      Swal.fire({
        icon: 'success',
        title: 'Envío Registrado',
        text: result.message || 'El envío de protección de valores se registró correctamente',
        timer: 2500,
        showConfirmButton: false
      });

      onGuardado(result.data);
    } catch (err) {
      console.error('Error al guardar PTV:', err);
      Swal.fire({ icon: 'error', title: 'Error al Guardar', text: err.message });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#1565c0', color: 'white' }}>
        <LocalShippingIcon />
        Nuevo Envío — Protección de Valores
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2 }}>
        <Grid container spacing={2}>
          {/* Sucursal */}
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={sucursales}
              getOptionLabel={(o) => o.nombreSucursal || ''}
              value={sucursalSeleccionada}
              onChange={(_, v) => setSucursalSeleccionada(v)}
              renderInput={(params) => <TextField {...params} label="Sucursal *" fullWidth />}
            />
          </Grid>

          {/* Empresa de servicio */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Empresa de Servicio *"
              fullWidth
              value={empresaServicio}
              onChange={(e) => setEmpresaServicio(e.target.value)}
              placeholder="Ej: Prosegur, Securitas..."
            />
          </Grid>

          {/* Cuenta bancaria */}
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={cuentasBancarias}
              getOptionLabel={(o) => o.cuentaBancaria ? `${o.nombreBanco ? o.nombreBanco + '-' : ''}${o.cuentaBancaria}${o.cuentaContableNew ? ` — ${o.cuentaContableNew}` : ''}` : ''}
              value={cuentaBancariaSeleccionada}
              onChange={(_, v) => setCuentaBancariaSeleccionada(v)}
              loading={loadingCuentas}
              renderOption={(props, o) => (
                <li {...props} key={o.idCuentaBancaria}>
                  <Box>
                    <Typography variant="body2">{o.cuentaBancaria}</Typography>
                    {o.cuentaContableNew && (
                      <Typography variant="caption" color="text.secondary">{o.cuentaContableNew}</Typography>
                    )}
                  </Box>
                </li>
              )}
              renderInput={(params) => <TextField {...params} label="Cuenta Bancaria *" fullWidth />}
            />
          </Grid>

          {/* Trazabilidad */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Código de Trazabilidad (Bolsa) *"
              fullWidth
              value={trazabilidad}
              onChange={(e) => setTrazabilidad(e.target.value)}
              placeholder="Ej: TV-20260317-001"
            />
          </Grid>

          {/* Fecha Envío */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Fecha de Envío *"
              type="date"
              fullWidth
              value={fechaEnvio}
              onChange={(e) => setFechaEnvio(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Observación */}
          <Grid item xs={12}>
            <TextField
              label="Observaciones"
              fullWidth
              multiline
              rows={2}
              value={observacion}
              onChange={(e) => setObservacion(e.target.value)}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Tabla de cierres revisados */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Cierres Disponibles (Revisados)
          </Typography>
          {cierresSeleccionados.length > 0 && (
            <Chip
              label={`Total seleccionado: ${formatNumber(totalEfectivoSeleccionado)}`}
              color="success"
              variant="outlined"
              sx={{ fontWeight: 'bold' }}
            />
          )}
        </Box>

        {!sucursalSeleccionada && (
          <Alert severity="info">Seleccione una sucursal para ver los cierres disponibles.</Alert>
        )}

        {sucursalSeleccionada && loadingCierres && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
            <CircularProgress size={30} />
          </Box>
        )}

        {sucursalSeleccionada && !loadingCierres && cierresRevisados.length === 0 && (
          <Alert severity="warning">No hay cierres revisados disponibles para esta sucursal.</Alert>
        )}

        {sucursalSeleccionada && !loadingCierres && cierresRevisados.length > 0 && (
          <TableContainer component={Paper} sx={{ maxHeight: 280 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={
                        cierresSeleccionados.length > 0 &&
                        cierresSeleccionados.length < cierresRevisados.length
                      }
                      checked={cierresSeleccionados.length === cierresRevisados.length}
                      onChange={toggleTodos}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Cód. Cierre</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Empleado / Usuario</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">Efectivo del Cierre</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cierresRevisados.map((cierre) => {
                  const seleccionado = cierresSeleccionados.some(c => c.idCierre === cierre.idCierre);
                  return (
                    <TableRow
                      key={cierre.idCierre}
                      hover
                      onClick={() => toggleCierre(cierre)}
                      sx={{ cursor: 'pointer', bgcolor: seleccionado ? '#e3f2fd' : 'inherit' }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={seleccionado}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggleCierre(cierre)}
                        />
                      </TableCell>
                      <TableCell>{cierre.codigoCierre || '-'}</TableCell>
                      <TableCell>{cierre.usuario}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {formatNumber(cierre.efectivo)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={guardando}>Cancelar</Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
          onClick={handleGuardar}
          disabled={guardando}
        >
          {guardando ? 'Guardando...' : 'Guardar Envío'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalNuevoPTV;
